/**
 * ValueIt — Compare page ApexCharts theme
 *
 * Shared base config for every chart in src/components/Graphs/ that's
 * rendered from src/pages/Compare/ (all ~21 of them currently build an
 * inline `options` object from scratch, each repeating things like
 * `dataLabels.style.colors: ["#000"]`). This file centralizes that
 * styling into one TradingView-ish visual language, so a chart
 * component only supplies what's actually specific to it: series/
 * categories data, its own chart.id/title, and any formatter functions
 * (currency/percent formatting varies per chart and stays local).
 *
 * Colors are passed as CSS custom-property strings (`"var(--vi-text)"`)
 * rather than hardcoded hex — ApexCharts renders them as literal
 * inline-style / SVG attribute values, so they resolve at paint time
 * against src/styles/theme-tokens.css exactly like a real CSS var
 * would. Where compare.theme.css can reach the same element via a
 * `!important` CSS override (axis text, gridlines, tooltip chrome —
 * see the APEXCHARTS section there), that CSS wins over whatever is
 * set here; the JS-side value below is just a sane non-CSS fallback
 * for the same look. Options that only exist as chart geometry
 * (stroke width/curve, bar radius, gradient fill, crosshairs) have no
 * CSS equivalent and must be set here.
 */
import merge from "lodash/merge";
import type { ApexOptions } from "apexcharts";

/**
 * Controlled series palette — the brand blue and a lighter tint of it
 * first (chart-1/2 in theme-tokens.css intentionally share one hue
 * rather than being two different blues; see that file's comment),
 * extended with a fixed set of complementary hues for charts with more
 * than two series (e.g. comparing several selected stocks). Deliberately
 * NOT `getRandomGraphColor()` — same chart should always assign the
 * same color to the same series position across renders.
 *
 * Pulled from --vi-chart-1..6 in theme-tokens.css rather than
 * --vi-accent/--vi-accent-2 — those get pushed darker under light mode
 * for text/button contrast, which would leave them too dark to pair
 * with a single dark data-label color across both themes. --vi-chart-*
 * stays in the same "medium-bright mark, dark label text on top" band
 * in both dark and light mode. See theme-tokens.css for the values and
 * the reasoning behind them (the extension hues are deliberately muted
 * rather than neon-bright — reported as hard on the eyes originally).
 */
export const viChartColors: string[] = [
  "var(--vi-chart-1)", // brand blue
  "var(--vi-chart-2)", // lighter tint of the brand blue
  "var(--vi-chart-3)", // muted indigo
  "var(--vi-chart-4)", // muted amber/gold
  "var(--vi-chart-5)", // muted slate-teal
  "var(--vi-chart-6)", // muted rose
];

/** Text color for data-label text sitting on top of a colored bar/
 *  slice/marker (not page background) — every chart used to repeat
 *  `dataLabels.style.colors: ["#000"]` (or `["#fff"]`) individually.
 *  --vi-chart-label-on is dark in both themes: the palette above stays
 *  medium-bright rather than neon or deep in either theme, so the same
 *  dark text reads cleanly on every series color regardless of mode. */
const dataLabelOnMarkColor = "var(--vi-chart-label-on)";

export const viChartBaseOptions: ApexOptions = {
  colors: viChartColors,
  chart: {
    background: "transparent",
    foreColor: "var(--vi-text-tertiary)",
    fontFamily: "var(--vi-font)",
    toolbar: { show: false },
    // Slower, gradual reveal on load/update instead of a near-instant
    // snap — reads as a smooth draw-in rather than a flash of chart.
    animations: {
      enabled: true,
      easing: "easeinout",
      speed: 800,
      animateGradually: {
        enabled: true,
        delay: 150,
      },
      dynamicAnimation: {
        enabled: true,
        speed: 400,
      },
    },
  },
  stroke: {
    curve: "smooth",
    width: 2.5,
  },
  /* Markers stay invisible until hover (size:0, prior behavior) — only
     the hover ring's color needed fixing: ApexCharts defaults to a
     white stroke there, which read as a stray white dot in dark mode.
     var(--vi-card-bg) makes it read as a "cut-out" ring in the mark's
     own color instead, in either mode. Same fix as
     DynamicGraphs/components/chartTheme.ts. */
  markers: {
    size: 0,
    strokeWidth: 2,
    strokeColors: "var(--vi-card-bg)",
    hover: { size: 5 },
  },
  grid: {
    borderColor: "var(--vi-card-border)",
    strokeDashArray: 3,
    xaxis: {
      lines: { show: false }, // no vertical grid lines — trading-terminal look
    },
    yaxis: {
      lines: { show: true },
    },
    padding: { top: 0, right: 8, bottom: 0, left: 8 },
  },
  xaxis: {
    axisBorder: { show: false },
    axisTicks: { show: false },
    crosshairs: {
      show: true,
      stroke: {
        color: "var(--vi-card-border)",
        width: 1,
        dashArray: 3,
      },
    },
    labels: {
      style: {
        colors: "var(--vi-text-tertiary)",
        fontSize: "11px",
        fontFamily: "var(--vi-font)",
      },
    },
  },
  yaxis: {
    axisBorder: { show: false },
    axisTicks: { show: false },
    labels: {
      style: {
        colors: "var(--vi-text-tertiary)",
        fontSize: "11px",
        fontFamily: "var(--vi-font)",
      },
    },
  },
  tooltip: {
    // ApexCharts' own light/dark theme keyword — left as "dark" in both
    // modes since it's just ApexCharts' fallback tooltip skin; the
    // .apexcharts-tooltip* rules in compare.theme.css `!important`
    // every color/border/shadow that's actually visible, in both
    // themes, so this keyword has nothing left to affect.
    theme: "dark",
    followCursor: true,
    marker: { show: true },
  },
  dataLabels: {
    style: {
      colors: [dataLabelOnMarkColor],
      fontSize: "10px",
      fontFamily: "var(--vi-font)",
      fontWeight: 600,
    },
    background: { enabled: false },
  },
  legend: {
    fontFamily: "var(--vi-font)",
    fontSize: "12px",
    labels: { colors: "var(--vi-text-secondary)" },
    markers: { width: 8, height: 8, radius: 8 },
    itemMargin: { horizontal: 10, vertical: 4 },
  },
};

/** Merged into bar charts only — rounded, modest-width columns with a
 *  subtle hover highlight instead of the library's chunky square default.
 *
 *  `fill` here matches DynamicGraphs' bar treatment: a vertical gradient
 *  fading OPACITY of the bar's own color (gradientToColors: viChartColors)
 *  rather than a flat block, for the same frosted-glass depth as the
 *  area fill below. */
const viBarPlotOptions: ApexOptions = {
  plotOptions: {
    bar: {
      borderRadius: 4,
      borderRadiusApplication: "end",
      columnWidth: "45%",
      dataLabels: { position: "top" },
    },
  },
  fill: {
    type: "gradient",
    gradient: {
      type: "vertical",
      gradientToColors: viChartColors,
      inverseColors: false,
      opacityFrom: 0.95,
      opacityTo: 0.35,
      stops: [0, 100],
    },
  },
  states: {
    hover: { filter: { type: "lighten", value: 0.1 } },
  },
};

/** Merged into line/area charts only — subtle fill fading toward the
 *  bottom instead of a flat opacity fill.
 *
 *  `gradientToColors: viChartColors` (rather than ApexCharts' own
 *  `shade`/`shadeIntensity` auto-derivation) is required, not optional,
 *  now that colors are "var(--vi-chart-1)" strings: the auto-derived
 *  mode computes the second gradient stop with the library's OWN
 *  internal color math, which can't parse a CSS variable string as a
 *  real color and silently produced a dark/grey fallback stop instead —
 *  invisible against this page's own dark-mode background, but a muddy
 *  grey smear against a light one (same bug, same fix, as
 *  DynamicGraphs/components/chartTheme.ts). Supplying the same color
 *  string for both stops sidesteps that math entirely — both stops just
 *  resolve the variable directly, the same way a plain fill color
 *  already does — and reads as a pure opacity fade, which is the
 *  correct "glass" look anyway (a tinted pane gets more see-through,
 *  it doesn't shift toward grey). */
const viAreaFillOptions: ApexOptions = {
  fill: {
    type: "gradient",
    gradient: {
      gradientToColors: viChartColors,
      opacityFrom: 0.35,
      opacityTo: 0.02,
      stops: [0, 90, 100],
    },
  },
};

/** Deep-merges chart-specific overrides onto the shared base. Use for
 *  chart types with no dedicated helper below (donut/pie — grid/xaxis/
 *  yaxis options are simply ignored by ApexCharts for those types, so
 *  merging the full base in is harmless). */
export const mergeChartOptions = (overrides: ApexOptions): ApexOptions =>
  merge({}, viChartBaseOptions, overrides);

/** Base + rounded-bar plot options, then chart-specific overrides. */
export const mergeBarChartOptions = (overrides: ApexOptions): ApexOptions =>
  merge({}, viChartBaseOptions, viBarPlotOptions, overrides);

/** Base + gradient area fill, then chart-specific overrides. */
export const mergeAreaChartOptions = (overrides: ApexOptions): ApexOptions =>
  merge({}, viChartBaseOptions, viAreaFillOptions, overrides);
